import React, { useCallback, useMemo, useRef } from 'react';
import { Spin, Tree, type DataNode } from '@bb/elements-web';
import type { CachedOrg } from '@bb/automaton';
import { debounce } from 'lodash';
import styled from 'styled-components';
import type { GroupData, GroupPreferences } from '../types';

type OrgData = Pick<CachedOrg, 'id' | 'locations' | 'name'>;

// depth 0, 1, 2
const FONT_SIZES = [18, 16, 14];
const FONT_WEIGHTS = [600, 500, 400];
/**
 * Build a unique tree key for a location node.
 */
const locationKey = (groupId: string, orgId: string, locIndex: number) =>
  `${groupId}__${orgId}__${locIndex}`;

/**
 * Build a unique tree key for an org node.
 */
const orgKey = (groupId: string, orgId: string) => `${groupId}__${orgId}`;

/**
 * Convert the groups data into antd DataNode[] tree structure and derive
 * the checked / half-checked keys from groupPreferences.
 */
const buildTreeData = (
  groups: GroupPreferencesTreeProps['groups'],
  groupPreferences: GroupPreferences,
) => {
  const treeData: (DataNode & { depth: number })[] = [];
  const checkedKeys: string[] = [];
  const halfCheckedKeys: string[] = [];

  groups.forEach(group => {
    const groupId = group.id;
    const orgNodes: (DataNode & { depth: number })[] = [];

    (group.orgs ?? []).forEach((org: OrgData) => {
      const locNodes: (DataNode & { depth: number })[] = [];
      let orgCheckedCount = 0;
      let orgTotalLocs = 0;

      org.locations.forEach((loc, locIdx) => {
        if (loc.isDeleted) return;
        orgTotalLocs += 1;

        const lKey = locationKey(groupId, org.id, locIdx);
        locNodes.push({
          key: lKey,
          title: loc.name,
          isLeaf: true,
          depth: 2,
        });

        const isChecked =
          groupPreferences[groupId]?.[org.id] === undefined ||
          !!groupPreferences[groupId]?.[org.id]?.[locIdx];
        if (isChecked) {
          checkedKeys.push(lKey);
          orgCheckedCount += 1;
        }
      });

      const oKey = orgKey(groupId, org.id);
      orgNodes.push({
        key: oKey,
        title: org.name,
        children: locNodes,
        depth: 1,
      });

      if (
        orgTotalLocs > 0 &&
        orgCheckedCount < orgTotalLocs &&
        orgCheckedCount > 0
      ) {
        halfCheckedKeys.push(oKey);
      }
    });

    treeData.push({
      key: groupId,
      title: group.label,
      children: orgNodes,
      depth: 0,
    });
  });

  return { treeData, checkedKeys, halfCheckedKeys };
};
interface GroupPreferencesTreeProps {
  groups: GroupData[];
  groupPreferences: GroupPreferences;
  loadingOrgs: boolean;
  hasMore: boolean;
  expandedKeys: string[];
  onExpand: (expandedKeys: string[]) => void;
  onCheck: (checkedKeys: string[]) => void;
  onLoadMore: () => void;
}

const GroupPreferencesTree: React.FC<GroupPreferencesTreeProps> = ({
  groups,
  groupPreferences,
  loadingOrgs,
  hasMore,
  expandedKeys,
  onExpand,
  onCheck,
  onLoadMore,
}) => {
  const loadingRef = useRef(false);

  const { treeData, checkedKeys } = useMemo(
    () => buildTreeData(groups, groupPreferences),
    [groups, groupPreferences],
  );

  const handleCheck = useCallback(
    (checked: string[] | { checked: string[]; halfChecked: string[] }) => {
      const keys = Array.isArray(checked) ? checked : checked.checked;
      onCheck(keys);
    },
    [onCheck],
  );

  const handleScroll = useMemo(
    () =>
      debounce((e: React.UIEvent<HTMLDivElement>) => {
        if (!hasMore || loadingRef.current) return;
        const target = e.target as HTMLDivElement;
        const { scrollTop, scrollHeight, clientHeight } = target;
        if (scrollTop + clientHeight >= scrollHeight * 0.8) {
          loadingRef.current = true;
          onLoadMore();
          // reset after a tick so next scroll can trigger again
          setTimeout(() => {
            loadingRef.current = false;
          }, 500);
        }
      }, 200),
    [hasMore, onLoadMore],
  );

  return (
    <TreeContainer onScroll={handleScroll}>
      <Tree
        dataCy="group-preferences-tree"
        checkable
        selectable={false}
        checkedKeys={checkedKeys}
        expandedKeys={expandedKeys}
        onExpand={onExpand as any}
        onCheck={handleCheck as any}
        treeData={treeData}
        titleRender={(node: DataNode & { depth?: number }) => (
          <span
            data-cy={`tree-node-${node.key}`}
            style={{
              fontSize: FONT_SIZES[node.depth ?? 0] ?? 14,
              fontWeight: FONT_WEIGHTS[node.depth ?? 0] ?? 400,
            }}
          >
            {node.title as string}
          </span>
        )}
      />
      {loadingOrgs && (
        <InfiniteLoadingContainer>
          <Spin dataCy="load-more-tree-data-spinner" size="small" />
          <span style={{ marginLeft: 8 }}>Loading Brokerages...</span>
        </InfiniteLoadingContainer>
      )}
    </TreeContainer>
  );
};

export default GroupPreferencesTree;

const TreeContainer = styled.div`
  &&& {
    height: 500px;
    max-height: 80vh;
    overflow: auto;
  }
`;

const InfiniteLoadingContainer = styled.div`
  &&& {
    text-align: center;
    padding: 12px;
  }
`;
